import { useLazyQuery } from "@apollo/client";
import { Autocomplete, debounce, TextField } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { REPORT_MEMBER_SEARCH } from "./dashboardReports.queries";

export interface PickedMember {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
}

const label = (member: PickedMember) => `${member.firstName} ${member.lastName} (${member.username})`.trim();

/** "Only this member": a name or username search that hands back the member. */
export const MemberPicker: React.FC<{
  value: PickedMember | null;
  onChange: (member: PickedMember | null) => void;
}> = ({ value, onChange }) => {
  const [input, setInput] = useState("");
  const [search, { data, loading }] = useLazyQuery(REPORT_MEMBER_SEARCH);
  const run = useMemo(() => debounce((filter: string) => search({ variables: { filter } }), 350), [search]);

  useEffect(() => {
    if (input.trim().length >= 2) run(input.trim());
  }, [input, run]);

  return (
    <Autocomplete<PickedMember>
      sx={{ minWidth: 260 }}
      options={input.trim().length >= 2 ? data?.searchUsers ?? [] : []}
      loading={loading}
      value={value}
      filterOptions={(options) => options}
      isOptionEqualToValue={(option, selected) => Number(option.id) === Number(selected.id)}
      getOptionLabel={label}
      noOptionsText={input.trim().length >= 2 ? "No member" : "Type a name or username"}
      onInputChange={(_, next) => setInput(next)}
      // The search answers with the GraphQL ID, a string; the reports' `userId` is an Int and refuses one.
      onChange={(_, member) => onChange(member && { ...member, id: Number(member.id) })}
      renderInput={(params) => <TextField {...params} label="Only this member" />}
    />
  );
};
